import React, { useState } from 'react';
import api from '@/api';
import { FileText, CalendarBlank, SpinnerGap, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const VCReports = ({ user, incubator = false }) => {
  const now = new Date();
  const [generating, setGenerating] = useState(false);
  const [startMonth, setStartMonth] = useState(now.getMonth() >= 2 ? now.getMonth() - 1 : 1);
  const [startYear, setStartYear] = useState(now.getFullYear());
  const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
  const [endYear, setEndYear] = useState(now.getFullYear());

  const handleGenerateLPReport = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/vc/reports/lp-report', {
        start_month: startMonth,
        start_year: startYear,
        end_month: endMonth,
        end_year: endYear
      }, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : 'LP_Report.pdf';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('LP Report downloaded!');
    } catch (err) {
      const detail = err.response?.status === 404 ? 'No portfolio companies found for this period' : 'Failed to generate report';
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const yearOptions = [];
  for (let y = 2020; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-heading font-bold text-text-primary mb-2" data-testid="reports-heading">Reports</h1>
        <p className="text-text-secondary mb-8">Generate portfolio reports for LPs and stakeholders.</p>

        {/* LP Report Card */}
        <div className="bg-surface border border-border rounded-[10px] p-6" data-testid="lp-report-card">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText size={24} className="text-primary" weight="bold" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-text-primary mb-1">{incubator ? 'Portfolio impact report' : 'LP Portfolio Report'}</h2>
              <p className="text-sm text-text-secondary">
                Full portfolio summary with fund-level aggregates and per-company breakdown including key metrics, monthly trends, benchmark rankings, and founder updates. Exported as a clean corporate PDF.
              </p>
            </div>
          </div>

          {/* Date Range Selector */}
          <div className="bg-background border border-border rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <CalendarBlank size={16} className="text-text-muted" weight="bold" />
              <span className="text-sm font-medium text-text-primary">Report Period</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">From</label>
                <div className="flex gap-2">
                  <select
                    value={startMonth}
                    onChange={(e) => setStartMonth(parseInt(e.target.value))}
                    className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                    data-testid="start-month"
                  >
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select
                    value={startYear}
                    onChange={(e) => setStartYear(parseInt(e.target.value))}
                    className="w-[90px] bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                    data-testid="start-year"
                  >
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">To</label>
                <div className="flex gap-2">
                  <select
                    value={endMonth}
                    onChange={(e) => setEndMonth(parseInt(e.target.value))}
                    className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                    data-testid="end-month"
                  >
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <select
                    value={endYear}
                    onChange={(e) => setEndYear(parseInt(e.target.value))}
                    className="w-[90px] bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                    data-testid="end-year"
                  >
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleGenerateLPReport}
            disabled={generating}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50"
            data-testid="generate-lp-report-btn"
          >
            {generating ? (
              <>
                <SpinnerGap size={18} className="animate-spin" weight="bold" />
                Generating...
              </>
            ) : (
              <>
                <DownloadSimple size={18} weight="bold" />
                {incubator ? 'Generate portfolio PDF' : 'Generate LP Report PDF'}
              </>
            )}
          </button>

          <p className="text-xs text-text-muted mt-3">
            Includes: Portfolio summary, per-company metrics with monthly trends, benchmark rankings, and founder qualitative updates.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VCReports;
